import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import { toastSuccess } from '@components/Toast';

import {
	fetchPriceRangeTableDataById,
	fetchProfitMarginTableDataById
} from '@store/actions/BrandProfileAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	getCategories as fetchAllCategories,
	getFabricTypes as fetchAllFabricTypes,
	getMarkets as fetchAllMarkets
} from '@store/design/action';
import { getAllCategories, getAllFabricTypes, getAllMarkets } from '@store/reducers';

import {
	deletePriceRange,
	onAddPriceRangeTableData,
	onUpdataePriceRangeTableData
} from '@services/crmService';

import { validateFloatNumber } from '@utils/Util';

const AddPriceRangeItemModal = ({
	saveType = 'add',
	onCloseModal,
	selectedCategoryIds,
	categoryResponseData,
	priceRangeIds
}) => {
	const [isLoading] = useState(false);
	const [selectedFabrics, setSelectedFabrics] = useState([
		{
			fabricType: {},
			priceRangeList: [
				{
					marketResponse: {},
					minPrice: null,
					maxPrice: null
				}
			]
		}
	]);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [deletedPriceRangeIds, setDeletedPriceRangeIds] = useState([]);
	const [error, setError] = useState(false);
	const { id } = useParams();
	const dispatch = useDispatch();

	let allCategories = useSelector(getAllCategories)?.map((item) => {
		return {
			id: item.id,
			value: item.name
		};
	});

	let allMarkets = useSelector(getAllMarkets)?.map((item) => {
		return {
			id: item.id,
			value: item.name
		};
	});

	let allFabricTypes = useSelector(getAllFabricTypes)?.map((item) => {
		return {
			id: item.code,
			value: item.value
		};
	});

	useEffect(() => {
		if (categoryResponseData) {
			setSelectedCategory(categoryResponseData?.categoryResponse);

			const updatedFabrics = categoryResponseData?.fabricPriceRangeList?.map((fabricData) => {
				const fabricType = allFabricTypes.find((fabric) => fabric.id === fabricData.fabricType);
				return {
					...fabricData,
					fabricType: fabricType || {}
				};
			});
			setSelectedFabrics(updatedFabrics);
		}
	}, [categoryResponseData]);

	useEffect(() => {
		dispatch(fetchAllMarkets());
		dispatch(fetchAllCategories(`?includeRangePlanCategory=true`));
		dispatch(fetchAllFabricTypes());
	}, []);

	const getAllCategoryOptions = () => {
		let allCategoriesList = allCategories || [];

		return allCategoriesList.filter((category) => !selectedCategoryIds.includes(category.id));
	};

	const getAllFabricTypeOptions = () => {
		let allFabricTypesList = allFabricTypes || [];
		let selectedFabricTypes = selectedFabrics.map((fabric) => fabric.fabricType?.id);

		return allFabricTypesList.filter((fabricType) => !selectedFabricTypes.includes(fabricType.id));
	};

	const getAllMarketOptions = (fabricIdx) => {
		const allMarketsList = allMarkets || [];
		const selectedMarkets =
			selectedFabrics[fabricIdx]?.priceRangeList.map((market) => market.marketResponse?.id) || [];

		return allMarketsList.filter((market) => !selectedMarkets.includes(market.id));
	};

	const addMarketGroup = (fabricIdx) => {
		const newMarketGroup = {
			marketResponse: {},
			minPrice: null,
			maxPrice: null
		};

		setSelectedFabrics((prevFabrics) =>
			prevFabrics.map((fabric, idx) =>
				idx === fabricIdx
					? { ...fabric, priceRangeList: [...fabric.priceRangeList, newMarketGroup] }
					: fabric
			)
		);
	};

	const removeMarketGroup = (fabricIdx, marketIdx) => {
		setSelectedFabrics((prevFabrics) =>
			prevFabrics.map((fabric, idx) =>
				idx === fabricIdx
					? { ...fabric, priceRangeList: fabric.priceRangeList.filter((_, i) => i !== marketIdx) }
					: fabric
			)
		);
	};

	const renderSelectedMarketGroup = (priceRangeList, fabricIdx) => {
		return priceRangeList?.map((item, marketIdx) => (
			<div className="market-group container pl-0" key={`market_${fabricIdx}_${marketIdx}`}>
				<div className="row">
					<div className="col-md-4 pr-6">
						<div className="form-group mb-2">
							<label htmlFor="market">Market</label>
							<CustomDropdown
								type="addItem"
								title="market"
								onItemClick={(data) => handleItemClick(data, fabricIdx, marketIdx)}
								items={getAllMarketOptions(fabricIdx)}
								selectedItem={item?.marketResponse?.name || 'Select'}
							/>
							{error[`market_${fabricIdx}_${marketIdx}`] && (
								<p className="error">{error[`market_${fabricIdx}_${marketIdx}`]}</p>
							)}
						</div>
					</div>
					<div className="col-md-4 pr-6 pl-6">
						<div className="form-group mb-2">
							<label htmlFor="minPrice">Lower Range</label>
							<input
								type="number"
								name="minPrice"
								value={item?.minPrice}
								onChange={(e) => handleFabricWiseInputChange(e, fabricIdx, marketIdx)}
								onKeyPress={validateFloatNumber}
								min={0}
							/>
							{error[`minPrice${fabricIdx}_${marketIdx}`] && (
								<p className="error">{error[`minPrice${fabricIdx}_${marketIdx}`]}</p>
							)}
							{error[`rangeValidation${fabricIdx}_${marketIdx}`] && (
								<p className="error">{error[`rangeValidation${fabricIdx}_${marketIdx}`]}</p>
							)}
						</div>
					</div>
					<div className="col-md-4 pl-6">
						<div className="form-group mb-2">
							<label htmlFor="maxPrice">Upper Range</label>
							<input
								type="number"
								name="maxPrice"
								value={item?.maxPrice}
								onChange={(e) => handleFabricWiseInputChange(e, fabricIdx, marketIdx)}
								onKeyPress={validateFloatNumber}
								min={0}
							/>
							{error[`maxPrice${fabricIdx}_${marketIdx}`] && (
								<p className="error">{error[`maxPrice${fabricIdx}_${marketIdx}`]}</p>
							)}
						</div>

						<button
							className="button text-only remove-market-group-row"
							onClick={() => removeMarketGroup(fabricIdx, marketIdx)}
							disabled={priceRangeList.length <= 1}
						>
							<img
								src={`${process.env.PUBLIC_URL}/icons/minus-round.svg`}
								alt="remove-icon"
								className="cursor-pointer"
							/>
						</button>
					</div>
				</div>
			</div>
		));
	};

	const renderSelectedFabrics = () => {
		return selectedFabrics?.map((fabric, fabricIdx) => {
			const isDisabled = fabric?.priceRangeList?.some((item) => item?.id);

			return (
				<div className="fabric-group mb-3" key={`fabric_${fabricIdx}`}>
					<div className="fabric-group-header d-flex justify-content-between">
						<p className="semibold-14">Fabric {fabricIdx + 1}</p>
						<button className="button text-only" disabled={selectedFabrics.length === 1}>
							<img
								src={`${process.env.PUBLIC_URL}/icons/delete_red.svg`}
								alt="delete"
								className="cursor-pointer"
								onClick={() => removeFabric(fabricIdx)}
							/>
						</button>
					</div>

					<div className="fabric-group-body">
						<div className="form-group mb-2">
							<label htmlFor="fabricType">Fabric Type</label>

							<CustomDropdown
								type="addItem"
								title="fabricType"
								onItemClick={(data) => handleItemClick(data, fabricIdx)}
								items={getAllFabricTypeOptions()}
								selectedItem={fabric?.fabricType?.value || 'Select'}
								isDisabled={isDisabled} // Disable dropdown if there's an ID in priceRangeList
							/>
							{error[`fabric_${fabricIdx}`] && (
								<p className="error">{error[`fabric_${fabricIdx}`]}</p>
							)}
						</div>

						<div className="all-market-groups mt-2">
							{renderSelectedMarketGroup(fabric?.priceRangeList, fabricIdx)}
						</div>

						<button
							className="button text size32"
							onClick={() => addMarketGroup(fabricIdx)}
							disabled={fabric?.priceRangeList?.length === allMarkets?.length}
						>
							<span className="weight-500 mr-2">Add </span>
							<img src={`${process.env.PUBLIC_URL}/icons/add-round.svg`} alt="add" />
						</button>
					</div>
				</div>
			);
		});
	};

	const addNewFabric = () => {
		setSelectedFabrics((prev) => [
			...prev,
			{
				fabricType: {},
				priceRangeList: [
					{
						marketResponse: {},
						minPrice: null,
						maxPrice: null
					}
				]
			}
		]);
	};

	const removeFabric = (index) => {
		setSelectedFabrics((prevFabrics) => prevFabrics.filter((_, i) => i !== index));
	};

	const handleClickCategory = (value) => {
		const categoryResponse = {
			id: value.id,
			name: value.value
		};
		setSelectedCategory(categoryResponse);
	};

	const handleItemClick = (data, fabricIdx, marketIdx) => {
		if (data.title === 'fabricType') {
			setSelectedFabrics((prevFabrics) =>
				prevFabrics.map((fabric, idx) =>
					idx === fabricIdx ? { ...fabric, fabricType: { value: data.value, id: data.id } } : fabric
				)
			);
		} else if (data.title === 'market') {
			setSelectedFabrics((prevFabrics) =>
				prevFabrics.map((fabric, idx) =>
					idx === fabricIdx
						? {
								...fabric,
								priceRangeList: fabric.priceRangeList.map((market, i) =>
									i === marketIdx
										? { ...market, marketResponse: { name: data.value, id: data.id } }
										: market
								)
							}
						: fabric
				)
			);
		}
	};
	const handleFabricWiseInputChange = (e, fabricIdx, marketIdx) => {
		const { name, value } = e.target;
		setSelectedFabrics((prevFabrics) =>
			prevFabrics.map((fabric, idx) =>
				idx === fabricIdx
					? {
							...fabric,
							priceRangeList: fabric.priceRangeList.map((market, i) =>
								i === marketIdx ? { ...market, [name]: value } : market
							)
						}
					: fabric
			)
		);
	};

	const validateForm = () => {
		let cloneError = {};

		const isEmpty = (obj) => Object.keys(obj).length === 0;

		if (!selectedCategory) {
			cloneError.category = 'Category is required';
		}

		selectedFabrics.forEach((fabric, index) => {
			if (isEmpty(fabric.fabricType)) {
				cloneError[`fabric_${index}`] = `Fabric type is required for fabric ${index + 1}`;
			}

			if (!fabric.priceRangeList || fabric.priceRangeList.length === 0) {
				cloneError[`market_${index}`] = `At least one market is required for fabric ${index + 1}`;
			} else {
				fabric.priceRangeList.forEach((market, marketIndex) => {
					if (isEmpty(market.marketResponse)) {
						cloneError[`market_${index}_${marketIndex}`] = 'Market is required';
					}

					const minPrice = parseFloat(market.minPrice);
					const maxPrice = parseFloat(market.maxPrice);

					if (isNaN(minPrice)) {
						cloneError[`minPrice${index}_${marketIndex}`] = 'Lower range is required';
					}

					if (isNaN(maxPrice)) {
						cloneError[`maxPrice${index}_${marketIndex}`] = 'Upper range is required';
					}

					if (!isNaN(minPrice) && !isNaN(maxPrice) && minPrice >= maxPrice) {
						cloneError[`rangeValidation${index}_${marketIndex}`] =
							'Lower range must be less than upper range';
					}
				});
			}
		});

		setError(cloneError);
		return Object.keys(cloneError).length > 0;
	};

	const getDeletedPriceRangeIds = () => {
		const updatedPriceRangeIds = selectedFabrics?.flatMap((fabric) =>
			fabric.priceRangeList?.map((priceRange) => priceRange.id)
		);

		const deletedIds = priceRangeIds.filter((item) => !updatedPriceRangeIds.includes(item));

		setDeletedPriceRangeIds(deletedIds);
	};

	const formatDataForApi = (selectedFabrics, categoryId) => {
		const fabricAndPriceList = selectedFabrics.reduce((acc, fabric) => {
			const formattedMarkets = fabric.priceRangeList.map((market) => ({
				id: market.id || null,
				marketId: market.marketResponse.id,
				minPrice: Number(market.minPrice),
				maxPrice: Number(market.maxPrice),
				fabricType: fabric.fabricType.id
			}));
			return [...acc, ...formattedMarkets];
		}, []);

		return {
			categoryId,
			fabricAndPriceList
		};
	};

	const onSubmit = async () => {
		if (!validateForm()) {
			const formattedData = formatDataForApi(selectedFabrics, selectedCategory.id);
			try {
				dispatch(openLoader());

				if (saveType === 'edit') {
					await deletePriceRange(id, deletedPriceRangeIds);
					await onUpdataePriceRangeTableData(id, formattedData);
				} else {
					await onAddPriceRangeTableData(id, formattedData);
				}
				dispatch(fetchPriceRangeTableDataById(`${id}`));
				dispatch(fetchProfitMarginTableDataById(`${id}`));
				toastSuccess('Successfully updated');
				dispatch(closeLoader());
				onCloseModal();
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	useEffect(() => {
		if (selectedFabrics) {
			getDeletedPriceRangeIds();
		}
	}, [selectedFabrics]);

	return (
		<ModalContainer
			className="modal-right add-target-modal add-pricing-modal"
			onCloseModal={onCloseModal}
			isSaticBackdrop={true}
		>
			<div className="common-popup">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-title d-flex align-items-center">
						<h3 className="semibold-16 mb-0">{saveType === 'edit' ? 'Edit' : 'Add'} Item*</h3>
					</div>
					<div className="close-btn" onClick={onCloseModal}>
						<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
					</div>
				</div>
				<div className="common-popup-body">
					<div className="main-category">
						<div className="form-group">
							<label htmlFor="collection">Category</label>
							<CustomDropdown
								isLoading={isLoading}
								isShowLabel={false}
								toolTipLength={18}
								items={getAllCategoryOptions()}
								selectedItem={selectedCategory?.name || 'Select'}
								onItemClick={(value) => handleClickCategory(value)}
								isDisabled={saveType === 'edit'}
							/>

							{error.category && <p className="error">{error.category}</p>}
						</div>
						{renderSelectedFabrics()}

						<button
							className="button outline size32"
							onClick={addNewFabric}
							disabled={selectedFabrics?.length === allFabricTypes?.length}
						>
							<img src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`} alt="add" />
							<span>Add Fabric</span>
						</button>
					</div>
				</div>
				<div className="modal-footer justify-content-between">
					<div className="d-flex"></div>
					<div className="d-flex gap-2">
						<button className="button outline size32" onClick={onCloseModal}>
							Cancel
						</button>
						<button className="button size32" onClick={onSubmit}>
							Save
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddPriceRangeItemModal;
